<script setup>
  import {computed, ref} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentTextarea from "@components/Textarea/Textarea.vue";
  import {router, usePage} from "@inertiajs/vue3";
  import {MedButton, MedLink, MedSpinner} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "CryptDialogsRecovery",
  });

  const props = defineProps({
    privacy: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    logout: {
      type: Function,
      required: true,
    },
  });

  const page = usePage();

  const refCryptDialog = ref(null);

  const recoveryCodePlaintext = ref("");

  const validRecoveryCodeLength = computed(() => recoveryCodePlaintextLength.value === 64);
  const recoveryCodePlaintextLength = computed(() =>
    recoveryCodePlaintext.value !== null ? recoveryCodePlaintext.value.replaceAll(/\s+/g, "").length : 0,
  );

  const open = () => {
    refCryptDialog.value.open();
  };

  const close = () => {
    if (refCryptDialog.value) refCryptDialog.value.close();
  };

  const verifyRecoveryCode = () => {
    const callback = () => {
      props.privacy.setCurrentStatus("RECOVERY_PENDING");
      props.privacy.initCrypt("", "RECOVERY_VALID");
      // set first user as current user (as we are authenticated
      // via clientencryptionchecksum and don't know the user)
      props.privacy.setCurrentUser(page.props.client.users[0]);

      setTimeout(() => {
        close();
        router.get(route("privacyPassword.show", {user: page.props.client.users[0].id}));
      }, 500);
    };

    const errorCallback = () => {
      props.privacy.setErrorMessage("Der Recovery-Code ist nicht korrekt.");
      props.privacy.setCurrentStatus("RECOVERY_VERIFICATION_INVALID");
    };

    props.privacy.verifyRecoveryCode(callback, errorCallback, recoveryCodePlaintext.value);
  };

  const userRemembersPasswordAgain = () => {
    recoveryCodePlaintext.value = null;
    props.privacy.setErrorMessage(null);
    props.privacy.setCurrentStatus("VERIFICATION_PENDING");
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog ref="refCryptDialog" dialog-width="medium" :test-id="getTestId('recovery')">
    <template #title>
      <span class="hidden print:inline">MediCheck</span>
      Datenschutzpasswort
    </template>

    <template #content>
      <div class="print:hidden">
        <template v-if="isLoading">
          <div class="text-center">
            <MedSpinner size="3xl" :data-test="getTestId('recovery-dialog-spinner')" />
          </div>
        </template>

        <template v-else>
          <p>
            Den 64 Zeichen langen Wiederher&shy;stellungs&shy;schlüssel (Recovery-Code) haben Sie sich beim Einrichten
            des Datenschutzpassworts ausgedruckt oder gespeichert.
          </p>

          <ComponentTextarea
            v-model="recoveryCodePlaintext"
            label="Recovery-Code"
            :helper-text="
              recoveryCodePlaintextLength < privacy.getRecoveryCodeLength()
                ? 'noch ' + (privacy.getRecoveryCodeLength() - recoveryCodePlaintextLength) + ' Zeichen'
                : null
            "
            :validation="privacy.getErrorMessage()"
            :test-id="getTestId('recovery-dialog-code-plaintext')"
            @keyup.enter="verifyRecoveryCode"
          />

          <p class="mt-4">
            Sie können den Recovery-Code sowohl mit als auch ohne Leerzeichen eingeben.
            <br />
            Bitte beachten Sie, dass Sie zur Zeit Ihr Datenschutzpasswort nach Eingabe des
            Wiederher&shy;stellungs&shy;schlüssels noch nicht ändern können.
            <br />

            <MedLink
              as="button"
              variant="primary"
              data-test="layout-crypt-default-dialog-password-forgotten-back-link"
              @click.prevent="userRemembersPasswordAgain"
            >
              Ich erinnere mich wieder an mein Passwort
            </MedLink>
          </p>
        </template>
      </div>
    </template>

    <template #actions>
      <MedButton
        variant="primary"
        :disabled="!validRecoveryCodeLength"
        :data-test="getTestId('recovery-dialog-verify-code-button')"
        @click="verifyRecoveryCode"
        @keydown.enter="verifyRecoveryCode"
      >
        Mit Recovery-Code entschlüsseln
      </MedButton>

      <MedButton flat :data-test="getTestId('recovery-dialog-logout-button')" @click="logout">Abmelden</MedButton>
    </template>
  </ComponentDialog>
</template>
